
/* ============================================================
   FILTER BAR
   ============================================================ */

import { Box, Paper, Typography, Button, FormControl, MenuItem, Select, TextField, IconButton, InputAdornment } from "@material-ui/core";
import { KeyboardDatePicker } from '@material-ui/pickers';
import { CONTENT_TYPE_LABELS } from "./types";
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import FilterListOutlinedIcon from '@material-ui/icons/FilterListOutlined';
import SearchIcon from '@material-ui/icons/Search';

interface FilterBarProps {
  matchType: string;
  contentType: string;
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  onApplyDateRange: () => void;
  canApplyDateRange: boolean;
  dateRangeDisabled: boolean;

  onMatchTypeChange:
    (value: string) => void;

  onContentTypeChange:
    (value: string) => void;


  onClear: () => void;

  hasActiveFilters: boolean;
}


export const LocalSearchBar: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  return (
    <Box
      style={{
        padding: '8px 20px 0',
        flexShrink: 0,
        background: '#FFFFFF',
      }}
    >
      <TextField
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder="Search by title or IMDb id"
        variant="outlined"
        size="small"
        fullWidth
        InputProps={{
          style: {
            height: 34,
            fontSize: 12,
            background: '#F8FAFC',
          },
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                style={{
                  fontSize: 16,
                  color: '#94A3B8',
                }}
              />
            </InputAdornment>
          ),
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Clear search"
                onClick={() => onChange('')}
              >
                <CloseOutlinedIcon style={{ fontSize: 14 }} />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />
    </Box>
  );
};

export const FilterBar: React.FC<
  FilterBarProps
> = ({
  matchType,
  contentType,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onApplyDateRange,
  canApplyDateRange,
  dateRangeDisabled,
  onMatchTypeChange,
  onContentTypeChange,
  onClear,
  hasActiveFilters,
}) => {
const contentTypeOptions = [
  {
    value: 'ALL',
    label: 'All',
  },
  ...Object.entries(CONTENT_TYPE_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];
  return (
    <Box
      style={{
        padding:
          '0 20px 7px',
        flexShrink: 0,
        background:
          '#FFFFFF',
      }}
    >

      <Paper
        elevation={0}
        style={{
          minHeight: 42,
          padding:
            '4px 10px',
          display: 'flex',
          alignItems: 'center',
          border:
            '1px solid #D9E1EC',
          borderRadius: 6,
          background:
            '#FFFFFF',
        }}
      >

        {/* FILTER TITLE */}

        <Box
          display="flex"
          alignItems="center"
          style={{
            paddingRight: 13,
            marginRight: 3,
            borderRight:
              '1px solid #D9E1EC',
          }}
        >

          <FilterListOutlinedIcon
            style={{
              fontSize: 16,
              color:
                '#2563EB',
              marginRight: 6,
            }}
          />

          <Typography
            style={{
              fontSize: 11,
              fontWeight: 700,
              color:
                '#334155',
            }}
          >
            Filters
          </Typography>

        </Box>


        {/* MATCH TYPE */}

        <FilterSelect
          label="Match Type"
          value={matchType}
          onChange={
            onMatchTypeChange
          }
          options={[
            {
              value: 'ALL',
              label: 'All',
            },
            {
              value: 'TITLE_MATCH',
              label: 'Title Match',
            },
            {
              value: 'ORIGINAL_TITLE_MATCH',
              label:
                'Original Title',
            },
            {
              value: 'AKA_MATCH',
              label: 'AKA Match',
            },
            {
              value:
                'WORKING_TITLE_MATCH',
              label:
                'Working Title',
            },
            {
              value: 'IMDB_MATCH',
              label: 'IMDb Match',
            },
            {
              value:
                'TITLE_CONTENT_TYPE_MATCH',
              label:
                'Title + Content Type',
            },
          ]}
        />


        {/* CONTENT TYPE */}
        <FilterSelect
          label="Content Type"
          value={contentType}
          onChange={onContentTypeChange}
          options={contentTypeOptions}
        />

        <DateRangeField
          label="From"
          value={startDate}
          maxDate={endDate || undefined}
          disabled={dateRangeDisabled}
          onChange={onStartDateChange}
        />

        <DateRangeField
          label="To"
          value={endDate}
          minDate={startDate || undefined}
          disabled={dateRangeDisabled}
          onChange={onEndDateChange}
        />

        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={dateRangeDisabled || !canApplyDateRange}
          onClick={onApplyDateRange}
          style={{
            marginLeft: 12,
            height: 31,
            textTransform: 'none',
            fontSize: 10,
            fontWeight: 600,
            boxShadow: 'none',
          }}
        >
          Apply
        </Button>

        {/* CLEAR */}

        {hasActiveFilters && (

          <Button
            size="small"
            startIcon={
              <CloseOutlinedIcon />
            }
            onClick={onClear}
            style={{
              marginLeft: 12,
              height: 28,
              color:
                '#64748B',
              textTransform:
                'none',
              fontSize: 10,
            }}
          >
            Clear
          </Button>

        )}

      </Paper>

    </Box>
  );
};




/* ============================================================
   DATE RANGE FIELD
   ============================================================ */

interface DateRangeFieldProps {
  label: string;
  value: Date | null;
  minDate?: Date;
  maxDate?: Date;
  disabled: boolean;
  onChange: (date: Date | null) => void;
}

const DateRangeField: React.FC<DateRangeFieldProps> = ({
  label,
  value,
  minDate,
  maxDate,
  disabled,
  onChange,
}) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      style={{
        marginLeft: 17,
      }}
    >
      <Typography
        style={{
          marginRight: 6,
          whiteSpace: 'nowrap',
          fontSize: 10,
          color: '#475569',
        }}
      >
        {label}:
      </Typography>

      <KeyboardDatePicker
        autoOk
        disableToolbar
        variant="inline"
        inputVariant="outlined"
        format="dd/MM/yyyy"
        margin="none"
        value={value}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        invalidDateMessage=""
        minDateMessage=""
        maxDateMessage=""
        onChange={onChange}
        KeyboardButtonProps={{
          size: 'small',
          style: { padding: 4 },
        }}
        InputProps={{
          style: {
            height: 31,
            fontSize: 11,
            width: 148,
          },
        }}
        style={{
          margin: 0,
        }}
      />
    </Box>
  );
};

/* ============================================================
   FILTER SELECT
   ============================================================ */

interface FilterSelectProps {
  label: string;
  value: string;
  onChange:
    (value: string) => void;
  options: Array<{
    value: string;
    label: string;
  }>;
}


const FilterSelect: React.FC<
  FilterSelectProps
> = ({
  label,
  value,
  onChange,
  options,
}) => {

  const handleChange = (
    event: React.ChangeEvent<{
      name?: string;
      value: unknown;
    }>
  ) => {
    onChange(event.target.value as string);
  };


  return (
    <Box
      display="flex"
      alignItems="center"
      style={{
        marginLeft: 17,
      }}
    >

      <Typography
        style={{
          marginRight: 6,
          whiteSpace:
            'nowrap',
          fontSize: 10,
          color:
            '#475569',
        }}
      >
        {label}:
      </Typography>


      <FormControl
        variant="outlined"
        size="small"
        style={{
          minWidth: 132,
        }}
      >

        <Select
          value={value}
          onChange={
            handleChange
          }
          style={{
            height: 31,
            fontSize: 10,
          }}
        >

          {options.map(
            (option) => (

              <MenuItem
                key={
                  option.value
                }
                value={
                  option.value
                }
                style={{
                  fontSize: 10,
                }}
              >
                {option.label}
              </MenuItem>

            )
          )}

        </Select>

      </FormControl>

    </Box>
  );
};
